import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faCheckCircle,
  faBuilding,
  faUsers,
  faEnvelope,
  faSpinner,
  faTag,
} from '@fortawesome/free-solid-svg-icons';

// Volume pricing tiers — matches Stripe price config
const PRICING_TIERS = [
  { label: 'Starter',  range: '1 – 3 subaccounts',  qty: '1–3',  rate: 27, example: 3  },
  { label: 'Growth',   range: '4 – 10 subaccounts', qty: '4–10', rate: 24, example: 10 },
  { label: 'Scale',    range: '11+ subaccounts',    qty: '11+',  rate: 18, example: 15 },
];

function getActiveTier(count) {
  if (!count || count <= 3) return 0;
  if (count <= 10) return 1;
  return 2;
}

function calcTotal(count) {
  const tier = PRICING_TIERS[getActiveTier(count)];
  return count * tier.rate;
}

const Billing = () => {
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileRes = await fetch('/auth/profile', {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        if (profileRes.ok) {
          const data = await profileRes.json();
          setProfile(data);
          setSubscription(data.subscription || null);
        }
      } catch (_) {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const subaccountCount = profile?.subaccountCount || subscription?.subaccountCount || 0;
  const activeTierIndex = getActiveTier(subaccountCount);
  const activeTier = PRICING_TIERS[activeTierIndex];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div
        className="rounded-2xl p-6 text-white shadow-lg"
        style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 100%)' }}
      >
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faCreditCard} className="text-2xl opacity-80" />
          <div>
            <h1 className="text-2xl font-bold">Billing &amp; Subscription</h1>
            <p className="text-sm opacity-75 mt-0.5">Your current plan and usage</p>
          </div>
        </div>
      </div>

      {/* Subscription Status */}
      <div
        className="rounded-2xl shadow-md overflow-hidden"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.9)' }}
      >
        <div
          className="px-6 py-4"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 100%)' }}
        >
          <h2 className="text-white font-semibold text-lg flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            Current Subscription
          </h2>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-6 items-start">
            <div>
              <p className="text-sm text-gray-500 mb-1">Plan</p>
              <p className="text-xl font-bold text-gray-800">{activeTier.label}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Status</p>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                  subscription?.status === 'active' || subscription?.status === 'trialing'
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                {subscription?.status === 'active'
                  ? 'Active'
                  : subscription?.status === 'trialing'
                  ? 'Trial'
                  : 'Active'}
              </span>
            </div>
            {subscription?.currentPeriodEnd && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Renews</p>
                <p className="text-base font-semibold text-gray-700">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
            )}
            {subaccountCount > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Monthly Total</p>
                <p className="text-base font-semibold text-gray-700">
                  ${calcTotal(subaccountCount).toFixed(2)}/mo
                </p>
              </div>
            )}
          </div>

          {subaccountCount > 0 && (
            <div className="mt-5 inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2 text-sm text-indigo-800">
              <FontAwesomeIcon icon={faBuilding} />
              <span>
                <strong>{subaccountCount}</strong> subaccount{subaccountCount !== 1 ? 's' : ''} ×{' '}
                <strong>${activeTier.rate}.00</strong>/mo each = <strong>${calcTotal(subaccountCount).toFixed(2)}/mo</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Tiers */}
      <div
        className="rounded-2xl shadow-md overflow-hidden"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.9)' }}
      >
        <div
          className="px-6 py-4"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 100%)' }}
        >
          <h2 className="text-white font-semibold text-lg flex items-center gap-2">
            <FontAwesomeIcon icon={faTag} />
            Volume Pricing
          </h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">
            Price per subaccount is based on your <strong>total quantity</strong> — the same rate applies to all subaccounts (non-graduating).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PRICING_TIERS.map((tier, idx) => {
              const isActive = idx === activeTierIndex && subaccountCount > 0;
              return (
                <div
                  key={tier.label}
                  className={`rounded-xl border-2 p-5 transition-all ${
                    isActive
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg text-gray-800">{tier.label}</h3>
                    {isActive && (
                      <span className="text-xs font-semibold bg-indigo-600 text-white rounded-full px-2.5 py-0.5">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-extrabold text-indigo-700 mb-1">
                    ${tier.rate}
                    <span className="text-sm font-normal text-gray-500">/subaccount/mo</span>
                  </p>
                  <p className="text-sm text-gray-500 mb-3">{tier.range}</p>
                  <p className="text-xs text-gray-400">
                    e.g. {tier.example} subaccounts = ${(tier.example * tier.rate).toFixed(0)}/mo
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
            <FontAwesomeIcon icon={faUsers} className="mt-0.5 flex-shrink-0" />
            <span>
              <strong>Additional team user:</strong> $12.00/user/mo — billed separately per extra seat added to your account.
            </span>
          </div>
        </div>
      </div>

      {/* Contact support */}
      <div
        className="rounded-2xl shadow-md p-6 flex items-start gap-4"
        style={{ backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.9)' }}
      >
        <FontAwesomeIcon icon={faEnvelope} className="text-2xl text-primary-600 mt-0.5" />
        <div>
          <h3 className="font-semibold text-gray-800 mb-1">Need to change your plan?</h3>
          <p className="text-sm text-gray-500">
            Contact us at{' '}
            <a href="mailto:support@ghlwa.com" className="text-primary-600 hover:underline font-medium">
              support@ghlwa.com
            </a>{' '}
            and we'll update your subscription for you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Billing;

